import { getCarFullModel, getCarModel, type Car } from "@/data/cars";

export const SITE_URL = "https://carentals.ro";

export const carUrl = (car: Car) => `${SITE_URL}/rentals/${car.slug}`;

const absolute = (src: string) => (src.startsWith("http") ? src : `${SITE_URL}${src}`);

/** Title (<= 60 chars where possible) unique per car thanks to model + year + price. */
export function carTitle(car: Car) {
  const model = getCarFullModel(car);
  const candidates = [
    `Rent ${model} ${car.year} Bucharest — €${car.pricePerDay}/Day, No Deposit`,
    `Rent ${model} ${car.year} — €${car.pricePerDay}/Day, No Deposit`,
    `${model} ${car.year} Rental Bucharest — €${car.pricePerDay}/Day`,
    `${model} Rental — €${car.pricePerDay}/Day, No Deposit`,
  ];
  return candidates.find((c) => c.length <= 60) ?? candidates[candidates.length - 1]!;
}

/** Meta description built from real specs, kept within the 160-char search snippet limit. */
export function carDescription(car: Car) {
  const model = getCarFullModel(car);
  const specs = `${car.fuelType}, ${car.transmission}, ${car.seats} seats`;
  const candidates = [
    `Book the ${car.year} ${model} (${specs}) in Bucharest from €${car.pricePerDay}/day. Zero deposit, no credit card, free Otopeni pickup. Reserve in 2 minutes.`,
    `Book the ${car.year} ${model} (${specs}) from €${car.pricePerDay}/day in Bucharest. Zero deposit, no credit card, free Otopeni pickup.`,
    `Book the ${car.year} ${model} from €${car.pricePerDay}/day in Bucharest. Zero deposit, no credit card, insurance and free Otopeni pickup included.`,
    `Book the ${car.year} ${model} from €${car.pricePerDay}/day in Bucharest. Zero deposit, no credit card, free airport pickup.`,
  ];
  return candidates.find((c) => c.length <= 160) ?? candidates[candidates.length - 1]!;
}



/** Short use-case line derived from body type, fuel and transmission. */
export function carBestFor(car: Car) {
  const base: Record<string, string> = {
    Compact: "city driving and tight Bucharest parking",
    Coupe: "short trips where style matters more than luggage space",
    Crossover: "mixed city and countryside driving",
    "Premium Selection": "business travel and client meetings",
    Sedan: "business trips and long motorway drives",
    SUV: "families, luggage and trips outside Bucharest",
  };
  const use = base[car.bodyType] ?? "everyday driving in and around Bucharest";
  const gearbox =
    car.transmission === "Manual"
      ? "Manual gearbox, so it suits drivers comfortable with a clutch in city traffic."
      : "Automatic gearbox, which makes Bucharest traffic noticeably easier.";
  const fuel =
    car.fuelType === "Diesel"
      ? "Diesel engine keeps motorway consumption low on longer routes."
      : car.fuelType === "Electric"
        ? "Fully electric, so plan charging stops on longer routes."
        : `${car.fuelType} engine, easy to refuel anywhere in Romania.`;
  return { use, gearbox, fuel };
}

/** 100–180 word, car-specific copy assembled from real fleet data. */
export function carLongDescription(car: Car) {
  const model = getCarFullModel(car);
  const { use, gearbox, fuel } = carBestFor(car);
  return [
    `The ${car.year} ${model} in our Bucharest fleet is a ${car.bodyType.toLowerCase()} with ${car.seats} seats and ${car.transmission.toLowerCase()} transmission, rented at €${car.pricePerDay} per day.`,
    car.description.trim(),
    `${gearbox} ${fuel}`,
    `We hand it over at Bulevardul Regina Elisabeta 33 or at Otopeni Airport free of charge, or deliver it to another address in Bucharest for €20. The price already covers insurance, the rovinieta road tax and 24/7 roadside assistance, and you pay no security deposit — a debit card or cash is enough, no credit card required.`,
    `It is a good match for ${use}.`,
  ].join(" ");
}

export function carFaq(car: Car) {
  const model = getCarFullModel(car);
  return [
    {
      q: `Do I need a deposit or a credit card for the ${model}?`,
      a: `No. We rent the ${model} with zero deposit and you can pay by debit card or in cash — a credit card is not required. You only need a valid driver's licence and ID at pickup.`,
    },
    {
      q: `Where can I pick up the ${model} in Bucharest?`,
      a: `Pickup is free at our office on Bulevardul Regina Elisabeta 33 and free at Otopeni Airport. Delivery to any other address in Bucharest costs €20.`,
    },
    {
      q: `What is included in the €${car.pricePerDay} daily price?`,
      a: `Insurance, unlimited mileage inside Romania, the rovinieta road tax and 24/7 roadside assistance are all included in the daily rate.`,
    },
  ];
}

/** Single primary JSON-LD object for a vehicle page. */
export function carJsonLd(car: Car) {
  const vehicle: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Car",
    "@id": `${carUrl(car)}#vehicle`,
    name: getCarFullModel(car),
    url: carUrl(car),
    brand: { "@type": "Brand", name: car.brand },
    manufacturer: { "@type": "Organization", name: car.brand },
    model: getCarModel(car),
    vehicleModelDate: String(car.year),
    productionDate: String(car.year),
    itemCondition: car.isNew
      ? "https://schema.org/NewCondition"
      : "https://schema.org/UsedCondition",
    image: car.images.map(absolute),
    description: car.description.trim(),
    bodyType: car.bodyType,
    vehicleConfiguration: car.bodyType,
    vehicleTransmission: car.transmission,
    fuelType: car.fuelType,
    vehicleSeatingCapacity: car.seats,
    steeringPosition:
      car.drive === "Left"
        ? "https://schema.org/LeftHandDriving"
        : "https://schema.org/RightHandDriving",
    offers: {
      "@type": "Offer",
      price: car.pricePerDay,
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      url: carUrl(car),
      businessFunction: "https://schema.org/LeaseOut",
    },
  };
  // vehicleIdentificationNumber is only emitted when a real VIN exists — never fabricated.
  if (car.vin) vehicle["vehicleIdentificationNumber"] = car.vin;
  return vehicle;
}

export function carBreadcrumbJsonLd(car: Car) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Rentals", item: `${SITE_URL}/rentals` },
      { "@type": "ListItem", position: 3, name: getCarFullModel(car), item: carUrl(car) },
    ],
  };
}

/** Full head() payload for a vehicle route. */
export function carHead(car: Car) {
  const title = carTitle(car);
  const description = carDescription(car);
  const url = carUrl(car);
  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "product" },
      { property: "og:url", content: url },
    ],
    links: [{ rel: "canonical", href: url }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(carJsonLd(car)) },
      { type: "application/ld+json", children: JSON.stringify(carBreadcrumbJsonLd(car)) },
    ],
  };
}
